import { useEffect, useState } from 'react'
import { offline, onOfflineChange, syncNow } from '../lib/offline'
import { fieldLabel } from '../lib/auditCore'

// Offline mode (OFFLINE-PLAN.md): the bar on every page. "Offline - last synced HH:MM"; the changes waiting to sync;
// the ones that "need a look" (the record changed on the server meanwhile - both values, the user chooses); the ones the
// server refused ("not saved", with its reason); changes that wait for a new login (the token could not be renewed).
const hhmm = t => (t ? new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : null)
const show = v => (v === null || v === undefined || v === '' ? '(empty)' : typeof v === 'object' ? JSON.stringify(v).slice(0, 80) : String(v).slice(0, 80))
export const offlineLine = (st, now = Date.now()) => {
  if (!st || !st.offline) return ''
  const t = hhmm(st.lastSync)
  return 'Offline - ' + (t ? 'last synced ' + t + (now - st.lastSync > 864e5 ? ' (' + new Date(st.lastSync).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ')' : '') : 'nothing synced on this device yet')
}

export default function OfflineBar({ uid }) {
  const [st, setSt] = useState(null), [open, setOpen] = useState(false), [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!uid) return
    let live = true
    const load = () => offline.status(uid).then(s => { if (live) setSt(s) }).catch(() => {})
    load()
    const off = onOfflineChange(load), t = setInterval(load, 15000)
    return () => { live = false; off(); clearInterval(t) }
  }, [uid])
  if (!uid || !st) return null
  const n = st.waiting.length, c = st.conflicts.length, f = st.failed.length
  if (!st.offline && !n && !c && !f && !st.blocked) return null
  const pill = (bg, fg) => ({ background: bg, color: fg, border: 'none', borderRadius: 12, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' })
  const btn = { background: 'none', border: '0.5px solid var(--border)', color: 'var(--text)', borderRadius: 6, padding: '3px 9px', fontSize: 12, cursor: 'pointer' }
  const act = async fn => { setBusy(true); try { await fn() } finally { setBusy(false); setSt(await offline.status(uid)) } }
  return (
    <div className="hc-offline-bar" style={{ margin: '0 0 8px', padding: '7px 12px', borderRadius: 10, background: st.offline ? 'rgba(201,147,58,.14)' : 'var(--hull, rgba(255,255,255,.04))', border: '0.5px solid ' + (st.offline ? 'var(--gold2, #8a5c0a)' : 'var(--border)'), fontSize: 12.5 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {st.offline && <b>{offlineLine(st)}</b>}
        {n > 0 && <button style={pill('rgba(77,132,255,.18)', 'var(--text)')} onClick={() => setOpen(o => !o)}>{n} {n === 1 ? 'change' : 'changes'} waiting to sync</button>}
        {c > 0 && <button style={pill('#8a5c0a', '#000')} onClick={() => setOpen(true)}>{c} {c === 1 ? 'needs' : 'need'} a look</button>}
        {f > 0 && <button style={pill('rgba(201,59,71,.2)', 'var(--text)')} onClick={() => setOpen(true)}>{f} not saved</button>}
        {st.blocked && n > 0 && <span style={{ color: 'var(--red, #C93B47)' }}>{st.blocked}</span>}
        {!st.offline && n > 0 && <button style={btn} disabled={busy} onClick={() => act(syncNow)}>{busy ? 'Syncing…' : 'Sync now'}</button>}
      </div>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {st.waiting.map(i => <div key={i.seq} style={{ color: 'var(--muted)' }}>{'⏳ '}{i.label} <span style={{ fontSize: 11 }}>{'· ' + hhmm(i.at)}</span></div>)}
          {st.conflicts.map(i => (
            <div key={i.seq} style={{ padding: 8, borderRadius: 8, border: '0.5px solid var(--gold2, #8a5c0a)' }}>
              <div style={{ fontWeight: 600 }}>{i.label} <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>{'· made ' + hhmm(i.at)}</span></div>
              <div style={{ color: 'var(--muted)', margin: '3px 0' }}>Changed on the server since this device read it - nothing was overwritten. Choose:</div>
              {(i.conflict || []).map(x => <div key={x.field}>{fieldLabel(x.field)}: <b>server</b> {show(x.server)} {'·'} <b>yours</b> {show(x.mine)} <span style={{ color: 'var(--muted)' }}>(was {show(x.base)})</span></div>)}
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button style={btn} disabled={busy} onClick={() => act(async () => { await offline.resolve(uid, i.seq, 'mine'); await syncNow() })}>{i.kind === 'rpc' ? 'Apply my change to the current stock' : 'Keep mine'}</button>
                <button style={btn} disabled={busy} onClick={() => act(() => offline.resolve(uid, i.seq, 'server'))}>Keep the server{'’'}s</button>
              </div>
            </div>
          ))}
          {st.failed.map(i => (
            <div key={i.seq} style={{ padding: 8, borderRadius: 8, border: '0.5px solid rgba(201,59,71,.5)' }}>
              <div style={{ fontWeight: 600 }}>Not saved: {i.label}</div>
              <div style={{ color: 'var(--muted)', margin: '3px 0' }}>{i.error}</div>
              <button style={btn} onClick={() => act(() => offline.dismiss(uid, i.seq))}>Dismiss</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
